import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import qs from "qs";
import PropTypes from "prop-types";
import { Alert, AvatarLabelGroup, Badge, Button, Card, DotsVertical, Download01, Eye, Menu, Plus, SearchLg, Select, Table, TextInput } from "@opendate-app/setlist";
import AddComplimentaryRegistrationsModal from "../AddComplimentaryRegistrationsModal";
import initialsFor from "../../ClassesShared/initials";
import ClassesTableFooter from "../../ClassesShared/ClassesTableFooter";
import personColorProps from "../../ClassesShared/personColor";
import { registrationStatusBadge } from "../constants/registrationStatus";
import registrationAmount from "../registrationAmount";
import { formatRegisteredAt } from "../../ClassesShared/classLabels";

const SPOTS_LEFT_ID = "class-registrations-spots-left";
const ALL_STATUSES = { value: "", label: "All statuses" };

const rosterQuery = (search, status) => {
  const q = {};
  if (search.trim()) q.full_name_or_email_cont = search.trim();
  if (status) q.status_eq = status;
  return q;
};

export default function RegistrationsTab({ registrationsPath, complimentaryPath, csrfToken, currencyCode, timeZone, onChange = () => {}, onHeaderActions = () => {} }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    try {
      const resp = await axios.get(registrationsPath, {
        params: { format: "json", q: rosterQuery(search, status), page },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "brackets" }),
      });
      if (requestId !== latestRequest.current) return;
      if (page > 1 && resp.data.classRegistrations.length === 0) {
        setPage(page - 1);
        return;
      }
      setData(resp.data);
      setError(null);
    } catch (e) {
      if (requestId !== latestRequest.current) return;
      setError("Could not load registrations.");
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [registrationsPath, search, status, page]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const registrations = data?.classRegistrations || [];
  const spotsLeft = data?.spotsRemaining ?? 0;

  const openRegistration = (registration) => { window.location.href = `${registrationsPath}/${registration.id}`; };
  const exportQuery = qs.stringify({ q: rosterQuery(search, status) }, { arrayFormat: "brackets" });
  const exportUrl = `${registrationsPath}.csv${exportQuery ? `?${exportQuery}` : ""}`;
  const pagination = data?.pagination;
  const total = pagination?.total_entries ?? registrations.length;
  const perPage = pagination?.per_page || registrations.length || 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min((page - 1) * perPage + registrations.length, total);
  const totalPages = pagination?.total_pages || 1;
  const canAddComplimentary = Boolean(data?.canAddComplimentary);

  useLayoutEffect(() => {
    onHeaderActions(
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
        <Button as="a" href={exportUrl} variant="secondary" icon={Download01}>
          Export CSV
        </Button>
        {canAddComplimentary && (
          <Button variant="primary" icon={Plus} onClick={() => setAdding(true)} disabled={spotsLeft === 0} aria-describedby={SPOTS_LEFT_ID}>
            Add Registration
          </Button>
        )}
      </div>,
    );
  }, [onHeaderActions, exportUrl, canAddComplimentary, spotsLeft]);

  useLayoutEffect(() => () => onHeaderActions(null), [onHeaderActions]);

  const columns = [
    {
      key: "name",
      header: "Learner",
      primary: true,
      render: (registration) => (
        <a href={`${registrationsPath}/${registration.id}`} className="text-reset text-decoration-none">
          <AvatarLabelGroup
            size="sm"
            initials={initialsFor(registration.full_name)}
            {...personColorProps(registration.full_name)}
            name={registration.full_name}
            supportingText={registration.primary_contact ? `${registration.email} · Group contact` : registration.email}
          />
        </a>
      ),
    },
    { key: "order", header: "Order #", render: (registration) => (registration.order_number ? `#${registration.order_number}` : "—") },
    { key: "created_at", header: "Registered", render: (registration) => formatRegisteredAt(registration.created_at, timeZone) },
    { key: "amount", header: "Amount", render: (registration) => registrationAmount(registration, currencyCode) },
    {
      key: "status",
      header: "Status",
      render: (registration) => {
        const badge = registrationStatusBadge(registration);
        return <Badge color={badge.color} size="sm" dot>{badge.label}</Badge>;
      },
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      render: (registration) => (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${registration.full_name}`} />}
          items={[{ label: "View Registration", icon: Eye, onClick: () => openRegistration(registration) }]}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 12 }}>
        <div className="flex-grow-1" style={{ maxWidth: 320, minWidth: 220 }}>
          <TextInput
            aria-label="Search registrations"
            leadingIcon={SearchLg}
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select
            aria-label="Filter by status"
            options={[ALL_STATUSES, ...(data?.statusOptions || [])]}
            value={status}
            onChange={(value) => { setStatus(value || ""); setPage(1); }}
          />
        </div>
        {canAddComplimentary && (
          <span id={SPOTS_LEFT_ID} className="classes-registrations-toolbar__spots ml-auto classes-text-secondary small">{`${spotsLeft} ${spotsLeft === 1 ? "spot" : "spots"} left`}</span>
        )}
      </div>

      {error ? (
        <Alert color="error" role="alert" description={error} />
      ) : (
        <Card
          padding="none"
          footer={(
            <ClassesTableFooter
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              total={total}
              noun="registrations"
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        >
          <Table className="classes-table"
            columns={columns}
            data={registrations}
            loading={loading}
            getRowKey={(registration) => registration.id}
            stackBelow={640}
            empty={search.trim() || status ? "No registrations match your search." : "No registrations yet."}
          />
        </Card>
      )}

      {adding && (
        <AddComplimentaryRegistrationsModal
          path={complimentaryPath}
          csrfToken={csrfToken}
          spotsLeft={spotsLeft}
          maxPerOrder={data.maxPerOrder}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); load(); onChange(); }}
        />
      )}

    </div>
  );
}

RegistrationsTab.propTypes = {
  registrationsPath: PropTypes.string.isRequired,
  complimentaryPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  currencyCode: PropTypes.string,
  timeZone: PropTypes.string,
  onChange: PropTypes.func,
  onHeaderActions: PropTypes.func,
};
